import {
  getGenerationForecast14DayData,
  getGenerationForecast3YearData,
} from "api/dataVisualisation/generationForecast/summary/summary";
import { getAllFuelTypes } from "api/dataVisualisation/reference/reference";
import useRequest, { RequestStatus } from "hooks/useRequest";
import { ForecastFilterOption } from "models/filterModels/forecastFilterOption";
import { GenerationForecastModel } from "models/generationForecast/generationForecastModel";
import { useCallback } from "react";
import { compareForecastDate } from "utils/generationForecastModelHelpers";
import { combineRequestStatuses } from "utils/requestHelpers";

const getGenerationForecast2To52WeekData = async (): Promise<
  GenerationForecastModel[]
> => {
  const data = await getGenerationForecast3YearData();
  data.sort(compareForecastDate);
  // The zero-index week is week 2 (2-52 weeks ahead)
  return data.slice(0, 50);
};

const useGenerationForecastChartContainerData = (
  forecastFilter: ForecastFilterOption
): {
  fetchData: () => void;
  data: {
    generationForecastData: GenerationForecastModel[];
    fuelTypes: string[];
  } | null;
  status: RequestStatus;
} => {
  const {
    data: fuelTypes,
    status: fuelTypesStatus,
    request: fetchFuelTypes,
  } = useRequest(getAllFuelTypes);

  const getGenerationForecastRequest = (): (() => Promise<
    GenerationForecastModel[]
  >) => {
    switch (forecastFilter) {
      case ForecastFilterOption.twoToFiftyTwoWeek:
        return getGenerationForecast2To52WeekData;
      case ForecastFilterOption.twoToOneFiveSixWeek:
        return getGenerationForecast3YearData;
      case ForecastFilterOption.twoToFourteenDay:
      default:
        return getGenerationForecast14DayData;
    }
  };

  const {
    data: generationForecastData,
    status: generationForecastStatus,
    request: fetchGenerationForecastData,
  } = useRequest(getGenerationForecastRequest());

  const fetchData = useCallback(() => {
    if (!fuelTypes?.length) {
      fetchFuelTypes();
    }
    fetchGenerationForecastData();
  }, [fetchFuelTypes, fetchGenerationForecastData, fuelTypes?.length]);

  const data =
    fuelTypes && generationForecastData
      ? {
          fuelTypes,
          generationForecastData,
        }
      : null;

  const status = combineRequestStatuses(
    fuelTypesStatus,
    generationForecastStatus
  );

  return {
    data,
    status,
    fetchData,
  };
};

export default useGenerationForecastChartContainerData;
